import { LegalPageLayout } from '../components';
import { Button } from '@/components/ui/button';
import { reopenBanner } from '@/lib/cookie-consent';

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="September 22, 2026">
      <p>
        Fias Inc. (&quot;Fias,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates
        the Fias platform. This Privacy Policy explains how we collect, use, disclose, and protect
        your personal information when you use our Service.
      </p>
      <p>
        By using the Service, you agree to the collection and use of information in accordance with
        this Privacy Policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Information You Provide</h3>
      <ul>
        <li>
          <strong>Account information:</strong> username, email address, and password when you
          create an account
        </li>
        <li>
          <strong>Content:</strong> messages, conversations, Arches, plugins, and other content you
          create or submit through the Service
        </li>
        <li>
          <strong>Communications:</strong> messages you send through chat channels, direct messages,
          and guilds
        </li>
        <li>
          <strong>Payment information:</strong> credit purchase history, subscription status,
          transaction records, and payment method metadata (card brand and last four digits). We do
          not store full payment card numbers, CVVs, or bank account details -- these are processed
          and stored exclusively by our payment processors (Stripe and Coinbase Commerce).
        </li>
        <li>
          <strong>Creator payout information:</strong> if you set up payouts as a creator, we
          collect identity verification data (processed by Stripe Connect), payout history, and
          earnings records. Tax identification information (SSN/EIN) may be collected as required
          for 1099 reporting under US law.
        </li>
        <li>
          <strong>Support requests:</strong> information you provide when contacting us for support
        </li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <ul>
        <li>
          <strong>Usage data:</strong> pages visited, features used, interactions with the Service,
          and timestamps
        </li>
        <li>
          <strong>Device information:</strong> browser type, operating system, and device
          identifiers
        </li>
        <li>
          <strong>Log data:</strong> IP address, access times, and error logs
        </li>
        <li>
          <strong>AI usage data:</strong> AI model provider, model name, token counts, and
          performance metrics associated with your use of AI-powered features
        </li>
      </ul>

      <h3>Cookies and Similar Technologies</h3>
      <p>
        We use cookies, local storage, and similar technologies to operate the Service. These fall
        into two categories:
      </p>
      <ul>
        <li>
          <strong>Strictly necessary:</strong> required for authentication, session management, and
          security. These are always on.
        </li>
        <li>
          <strong>Optional:</strong> used to remember your preferences and analyze how the Service
          is used. In the EEA and United Kingdom, we set these only with your consent, which you can
          manage or withdraw at any time through our cookie preferences.
        </li>
      </ul>
      <p>
        You can also control cookies through your browser settings; disabling them may limit your
        ability to use parts of the Service.
      </p>
      <p className="not-prose">
        <Button variant="outline" size="sm" onClick={reopenBanner}>
          Manage cookie preferences
        </Button>
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service</li>
        <li>Process transactions and manage your account</li>
        <li>
          Send you service-related communications (e.g., account verification, security alerts)
        </li>
        <li>Enforce our Terms of Service and Acceptable Use Policy</li>
        <li>Respond to your requests and provide support</li>
        <li>Monitor and analyze usage patterns to improve the Service</li>
        <li>Detect, prevent, and address technical issues, fraud, and abuse</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        <strong>We do not use your content, conversations, or inputs to train AI models.</strong>
      </p>

      <h2>3. Legal Bases for Processing (EEA/UK Users)</h2>
      <p>
        If you are located in the European Economic Area (EEA) or the United Kingdom, we process
        your personal information on the following legal bases:
      </p>
      <ul>
        <li>
          <strong>Contract:</strong> to provide the Service, process payments, and fulfill our
          obligations under these Terms.
        </li>
        <li>
          <strong>Legitimate interests:</strong> to operate, secure, and improve the Service; to
          detect and prevent fraud and abuse; and to communicate with you about your account.
        </li>
        <li>
          <strong>Legal obligation:</strong> to comply with applicable laws, including tax,
          accounting, and anti-money-laundering requirements.
        </li>
        <li>
          <strong>Consent:</strong> for processing that requires your consent, such as non-essential
          cookies. You may withdraw your consent at any time.
        </li>
      </ul>

      <h2>4. How We Share Your Information</h2>
      <p>We may share your information in the following circumstances:</p>
      <ul>
        <li>
          <strong>Third-party AI providers:</strong> When you use AI-powered features, your inputs
          are sent to third-party AI providers (such as OpenAI, Anthropic, and Google) for
          processing. These providers process your data according to their own privacy policies.
        </li>
        <li>
          <strong>Payment processors:</strong> When you purchase credits or subscribe, your payment
          information is processed by Stripe (for card payments and subscriptions) or Coinbase
          Commerce (for cryptocurrency payments). These processors handle your financial data
          according to their own privacy policies. Stripe&apos;s privacy policy is available at
          stripe.com/privacy. Coinbase&apos;s privacy policy is available at
          coinbase.com/legal/privacy. For creator payouts, Stripe Connect processes identity
          verification and bank account information.
        </li>
        <li>
          <strong>Service providers:</strong> We use third-party services to help operate the
          Service, including cloud hosting (Amazon Web Services), authentication, and analytics.
          These providers access your information only as necessary to perform their services.
        </li>
        <li>
          <strong>Legal requirements:</strong> We may disclose your information if required by law,
          court order, or governmental regulation, or if we believe disclosure is necessary to
          protect our rights, your safety, or the safety of others.
        </li>
        <li>
          <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of
          assets, your information may be transferred to the acquiring entity.
        </li>
        <li>
          <strong>With your consent:</strong> We may share your information for purposes not
          described here with your explicit consent.
        </li>
      </ul>
      <p>We do not sell your personal information to third parties.</p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is active or as needed to
        provide the Service.
      </p>
      <p>
        <strong>Closing your account.</strong> When you close your account, we deactivate it and
        revoke access to the Service immediately, and we anonymize your request history. Content you
        have stored in the Service — such as documents, notes, and other material in your workspace
        — is not deleted automatically at closure. It is retained until you ask us to erase it, or
        until it is removed under a retention schedule, as described below.
      </p>
      <p>
        <strong>Requesting erasure.</strong> You may ask us to erase your personal information at
        any time, whether or not you close your account, by contacting us using the details in the
        &quot;Contact Us&quot; section. We will respond within 30 days, or as required by applicable
        law, and will delete or anonymize the information we hold, except where we are required or
        permitted to retain it for legal, regulatory, or legitimate business purposes — for example
        financial and transaction records, signed documents and the record of how they were signed
        (see below), or information needed to resolve a dispute or enforce our agreements.
      </p>
      <p>
        <strong>Backups and prior versions.</strong> Deleting content removes it from the active
        Service. Copies can remain for a limited period in encrypted backups and in retained prior
        versions of stored files, which exist so that data can be recovered after an incident or an
        accidental deletion. Those copies are not used to provide the Service and are overwritten in
        the ordinary course as the backup and version-retention windows elapse.
      </p>
      <p>Shared conversation links automatically expire 30 days after creation.</p>
      <p>
        <strong>Signed documents.</strong> When you sign a document through the Service, or send one
        for signature, the completed document and the record of how it was signed — who consented to
        electronic records and when, who viewed the document, and who signed it — are retained for
        seven years from the date the document is completed, and for longer where the law or the
        agreement governing that document requires it. These records are kept even if you close your
        account, and they are not removed on an erasure request. The other parties to a signed
        agreement rely on them as evidence of an agreement you are all still bound by, so erasing
        them at one party&apos;s request would destroy another party&apos;s evidence.
      </p>
      <p>
        Payment transaction records and credit transaction history are retained indefinitely for
        legal, accounting, and audit purposes, even after account closure.
      </p>

      <h2>6. Your Rights and Choices</h2>
      <p>Depending on your location, you may have the following rights:</p>
      <ul>
        <li>
          <strong>Access:</strong> Request a copy of the personal information we hold about you
        </li>
        <li>
          <strong>Deletion:</strong> Request erasure of your personal information, subject to the
          retention exceptions described in &quot;Data Retention&quot; above
        </li>
        <li>
          <strong>Correction:</strong> Request correction of inaccurate personal information
        </li>
        <li>
          <strong>Export:</strong> Export your conversation data in JSON or Markdown format through
          the Service
        </li>
        <li>
          <strong>Account closure:</strong> Close your account at any time through your account
          settings
        </li>
        <li>
          <strong>Restriction:</strong> Request that we restrict processing of your personal
          information in certain circumstances
        </li>
        <li>
          <strong>Objection:</strong> Object to processing based on our legitimate interests,
          including for direct marketing
        </li>
        <li>
          <strong>Withdraw consent:</strong> Where we rely on your consent, you may withdraw it at
          any time without affecting processing performed before withdrawal
        </li>
      </ul>
      <p>
        To exercise these rights, contact us at <a href="mailto:privacy@fias.io">privacy@fias.io</a>
        . We will respond to your request within 30 days, or as required by applicable law.
      </p>

      <h3>California Residents</h3>
      <p>
        If you are a California resident, you have additional rights under the California Consumer
        Privacy Act (CCPA), including the right to know what personal information we collect and how
        it is used, the right to request deletion, and the right to opt out of the sale of personal
        information. We do not sell personal information. To exercise your rights, contact us at{' '}
        <a href="mailto:privacy@fias.io">privacy@fias.io</a>.
      </p>

      <h3>EEA/UK Residents</h3>
      <p>
        If you are located in the EEA or the United Kingdom, you also have the right to lodge a
        complaint with your local data protection supervisory authority. UK residents may contact
        the Information Commissioner&apos;s Office at ico.org.uk.
      </p>

      <h2>7. Data Security</h2>
      <p>
        We implement reasonable technical and organizational measures to protect your personal
        information, including encryption in transit and at rest, access controls, and regular
        security assessments. However, no method of transmission over the Internet or electronic
        storage is completely secure, and we cannot guarantee absolute security.
      </p>

      <h2>8. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in the United States, where our servers
        are located. If you are accessing the Service from outside the United States, please be
        aware that your information may be subject to U.S. data protection laws, which may differ
        from those in your country.
      </p>
      <p>
        Where personal information is transferred from the EEA, United Kingdom, or Switzerland to
        the United States, we rely on the Standard Contractual Clauses approved by the European
        Commission (and the UK International Data Transfer Addendum, where applicable) as the legal
        mechanism for such transfers.
      </p>

      <h2>9. Do Not Track</h2>
      <p>
        Some browsers transmit a &quot;Do Not Track&quot; signal. Because there is no common
        industry standard for how to respond, the Service does not currently respond to Do Not Track
        signals.
      </p>

      <h2>10. Children&apos;s Privacy</h2>
      <p>
        The Service is not intended for anyone under 18 years of age. We do not knowingly collect
        personal information from children under 18. If we learn that we have collected information
        from a child under 18, we will take steps to delete that information promptly.
      </p>

      <h2>11. Third-Party Links and Services</h2>
      <p>
        The Service may contain links to third-party websites or integrate with third-party
        services. This Privacy Policy does not apply to those third parties. We encourage you to
        review the privacy policies of any third-party services you use.
      </p>

      <h2>12. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If we make material changes, we will
        notify you by posting the updated policy on the Service with a new &quot;Last Updated&quot;
        date. Your continued use of the Service after the changes take effect constitutes your
        acceptance of the revised policy.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or our data practices, please contact us at:
      </p>
      <p>
        Fias Inc.
        <br />
        Email: <a href="mailto:privacy@fias.io">privacy@fias.io</a>
      </p>
    </LegalPageLayout>
  );
}
